import { Router } from 'vue-router'

import { defineStore } from 'pinia'

import { useAuthStore } from './AuthStore'
import { useUserSettingsStore } from './UserSettingsStore'
import { useAuthorizationStore } from './AuthorizationStore'
import { useCallCenterStore } from './CallCenterStore'
import { useSocketStore } from './SocketStore'
import { useTaskStore } from './TaskStore'

// Types (matching the store types for facade compatibility)
interface LoginCredentials {
  e: string
  p: string
}

interface LoginResponse {
  success: boolean
  message?: string
}

interface VerifyResponse {
  success: boolean
  message: string | null
}

interface UserSettings {
  darkmode: string
  locale: string
  timezone: string
  clock24Hours: boolean
}

interface AccessAllowed {
  regions: string[]
  manufacturers: string[]
  groups: string[]
  dealerships: {
    id: number
    name: string
    manufacturers: number[]
  }[]
}

interface CallCenter {
  id: string
  name?: string
}

interface User {
  id: string
  type: string
  name: string
  activeCallCenter: number | null
  agentStatus: string
}

interface Conference {
  name: string
  direction: 'inbound' | 'outbound'
  participants: unknown[]
  status: string
  connectedAt?: string
  endedAt?: string
  endedReason?: string
  wrappedUpAt?: string
  wrapUpData?: unknown
  agentId?: string
}

/**
 * UserStore - Facade/Compatibility Layer
 *
 * This store acts as a facade that delegates to the new specialized stores
 * to maintain backward compatibility during migration.
 *
 * @deprecated This store is kept for backward compatibility.
 * New code should use the specific stores directly:
 * - useAuthStore() for authentication
 * - useUserSettingsStore() for settings
 * - useAuthorizationStore() for roles/permissions
 * - useCallCenterStore() for call centers
 * - useSocketStore() for socket/realtime
 * - useTaskStore() for tasks
 */
export const useUserStore = defineStore('userStore', {
  state: (): {
    router?: Router
  } => ({
    router: undefined
  }),

  getters: {
    // Auth getters
    details() {
      return useAuthStore().details
    },
    isLoggedIn() {
      return useAuthStore().isLoggedIn
    },
    isAuthenticating() {
      return useAuthStore().isAuthenticating
    },
    isVerifying() {
      return useAuthStore().isVerifying
    },

    // Settings getters
    userSettings(): UserSettings {
      return useUserSettingsStore().userSettings
    },

    // Authorization getters
    roles(): string[] {
      return useAuthorizationStore().roles
    },
    accessAllowed(): AccessAllowed {
      return useAuthorizationStore().accessAllowed
    },

    // Call center getters
    callCenters(): CallCenter[] {
      return useCallCenterStore().callCenters
    },
    selectedCallCenter(): string | null {
      return useCallCenterStore().selectedCallCenter
    },

    // Socket getters
    socket() {
      return useSocketStore().socket
    },
    socketIsConnected(): boolean {
      return useSocketStore().socketIsConnected
    },
    activeUsers(): User[] {
      return useSocketStore().activeUsers
    },
    usersPresence(): User[] {
      return useSocketStore().usersPresence
    },
    callCenterConferences(): Conference[] {
      return useSocketStore().callCenterConferences
    },
    agentStatus(): string {
      return useSocketStore().agentStatus
    },

    // Task getters
    outstandingTasks(): number | null {
      return useTaskStore().outstandingTasks
    },
    // Backward compatibility alias
    outstandingTodos(): number | null {
      return useTaskStore().outstandingTasks
    }
  },

  actions: {
    /**
     * Syncs router to AuthStore when UserStore router is set
     * This is called by the Pinia plugin in main.js
     */
    syncRouter(): void {
      if (this.router) {
        const authStore = useAuthStore()
        authStore.router = this.router
      }
    },

    /**
     * Updates all users in the store
     * @param users - Array of users to update
     */
    updateAllUsers(users: User[]): void {
      useSocketStore().updateAllUsers(users)
    },

    /**
     * Sets all user settings from database
     * @param settingsFromDB - Settings object from database
     */
    setAllSettings(settingsFromDB: UserSettings): void {
      useUserSettingsStore().setAllSettings(settingsFromDB)
    },

    /**
     * Updates user settings
     * @param newSettings - Array of setting objects with name and value properties
     */
    async updateUserSettings(newSettings: Array<{ name: string; value: unknown }>): Promise<void> {
      return useUserSettingsStore().updateUserSettings(newSettings)
    },

    /**
     * Updates user roles
     * @param roles - Array of role strings
     */
    updateRoles(roles: string[]): void {
      useAuthorizationStore().updateRoles(roles)
    },

    /**
     * Updates access allowed for user
     * @param access - Access object with regions, manufacturers, groups, and dealerships
     */
    updateAccessAllowed(access: AccessAllowed): void {
      useAuthorizationStore().updateAccessAllowed(access)
    },

    /**
     * Updates call centers and sets selected call center
     * @param callCenters - Array of call center objects
     */
    updateCallCenters(callCenters: CallCenter[]): void {
      useCallCenterStore().updateCallCenters(callCenters)
    },

    /**
     * Selects a call center
     * @param callCenterId - ID of the call center to select
     */
    selectCallCenter(callCenterId: string | null): void {
      useCallCenterStore().selectCallCenter(callCenterId)
    },

    /**
     * Logs in a user with credentials
     * @param credentials - Login credentials object
     * @param redirectTo - Route to redirect to after successful login
     * @returns Promise with login result
     */
    async login(credentials: LoginCredentials, redirectTo: string = '/'): Promise<LoginResponse> {
      const authStore = useAuthStore()
      authStore.router = this.router
      return authStore.login(credentials, redirectTo)
    },

    /**
     * Verifies user authentication
     * @returns Promise with verification result
     */
    async verify(): Promise<VerifyResponse> {
      const authStore = useAuthStore()
      authStore.router = this.router
      return authStore.verify()
    },

    /**
     * Signs out the user
     * @param routeToLogin - Whether to route to login page after signout
     */
    async signOut(routeToLogin: boolean = true): Promise<void> {
      const authStore = useAuthStore()
      authStore.router = this.router
      return authStore.signOut(routeToLogin)
    },

    /**
     * Initializes socket connection
     */
    initializeSocket(): void {
      useSocketStore().initializeSocket()
    },

    /**
     * Updates agent status
     * @param status - New agent status
     * @param userId - User ID to update status for
     */
    updateAgentStatus(status: string, userId?: string): void {
      useSocketStore().updateAgentStatus(status, userId)
    },

    /**
     * Sets user presence
     * @param presence - Presence status
     * @param userId - User ID to set presence for
     */
    setUserPresence(presence: string, userId: string): void {
      useSocketStore().setUserPresence(presence, userId)
    },

    /**
     * Triggers user activity
     */
    triggerUserActivity(): void {
      useSocketStore().triggerUserActivity()
    },

    /**
     * Submits call wrap up data
     * @param call - Call data to wrap up
     */
    submitCallWrapUp(call: unknown): void {
      useSocketStore().submitCallWrapUp(call)
    }
  }
})
